import React from 'react';
import { toast } from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  adminService,
  IAdminUser,
  IAdminProductLicense
} from '../service/admin.service';

const EYE_SLUG = 'squarebit-eye';

/**
 * The message the licence server sent, if it sent one. Every refusal in
 * LicensingService names the thing the admin has to do about it — how many
 * machines are activated, how many to release — and that instruction is worth
 * far more than "Couldn't update the licence". A transport failure has no
 * message and falls back to the generic string at the call site.
 */
const serverMessage = (err: unknown): string | undefined =>
  (err as { response?: { data?: { message?: unknown } } })?.response?.data
    ?.message as string | undefined;

/**
 * Matches @Max(1000) on AdminUpdateLicenseDto.seats. The admin ceiling is
 * deliberately far above the 99 the self-serve checkout allows: this is where
 * a Studio / site licence agreed by contract gets entered, which is exactly
 * the case the storefront refuses to sell.
 */
const MAX_ADMIN_SEATS = 1000;

/**
 * The seat count on one licence, with the number of seats currently held next
 * to it — an admin dropping the count needs to see what they are about to
 * strand before they do it, not after.
 *
 * A typed field rather than only the +/- steppers it replaces: moving a
 * licence from 2 seats to 40 was forty round trips to the server, each one a
 * separate audited seats_change event, and any one of them could fail and
 * leave the licence at a number nobody chose. One edit is one write is one
 * event.
 *
 * The server owns the floor (it refuses a count below the machines currently
 * activated) and this deliberately does NOT block the attempt — it warns, then
 * shows the server's own refusal, which names the number of machines to
 * release. A disabled button would leave the admin guessing at the rule.
 */
const SeatEditor = ({
  license,
  busy,
  onSave
}: {
  license: IAdminProductLicense;
  busy: boolean;
  onSave: (seats: number) => void;
}) => {
  const { t } = useTranslation();
  const [value, setValue] = React.useState(license.seats);

  // Re-sync when the server's number moves under us: a successful save, or
  // another admin's change arriving on a refetch. A REFUSED save leaves
  // license.seats where it was, so this does not fire and the typed number
  // stays on screen — which is what the admin wants, since the refusal toast
  // tells them to go release machines and come back to the same edit.
  React.useEffect(() => setValue(license.seats), [license.seats]);

  const clamp = (n: number) => Math.min(MAX_ADMIN_SEATS, Math.max(1, Math.trunc(n)));
  const dirty = value !== license.seats;
  const stranding = value < license.usedSeats;

  const stepper =
    'w-[24px] h-[24px] rounded-[6px] border border-border-dark/[0.15] text-[13px] leading-none text-content-muted hover:bg-surface-hover disabled:opacity-40 transition-colors';

  return (
    <>
      <span className="text-[12px] text-gray-900 dark:text-gray-700">
        {t('admin.dashboard.licenses.seats', {
          used: license.usedSeats,
          total: license.seats
        })}
      </span>
      <button
        type="button"
        aria-label={t('admin.dashboard.licenses.seatsDecrease')}
        disabled={busy || value <= 1}
        onClick={() => setValue((n) => clamp(n - 1))}
        className={stepper}
      >
        −
      </button>
      <input
        type="number"
        inputMode="numeric"
        min={1}
        max={MAX_ADMIN_SEATS}
        aria-label={t('admin.dashboard.licenses.seatsInput')}
        disabled={busy}
        value={value}
        onChange={(e) => {
          const next = Number(e.target.value);
          if (Number.isFinite(next)) setValue(Math.trunc(next));
        }}
        onBlur={() => setValue((n) => clamp(n))}
        className="w-[52px] text-center py-[2px] rounded-[6px] border border-border-dark/[0.15] bg-surface-base text-[12px] font-medium text-content-dark disabled:opacity-40"
      />
      <button
        type="button"
        aria-label={t('admin.dashboard.licenses.seatsIncrease')}
        disabled={busy || value >= MAX_ADMIN_SEATS}
        onClick={() => setValue((n) => clamp(n + 1))}
        className={stepper}
      >
        +
      </button>
      {dirty && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onSave(clamp(value))}
          className="inline-flex items-center px-[8px] py-[3px] rounded-[6px] text-[11px] font-medium bg-blue text-surface-base hover:bg-surface-popular transition-colors disabled:opacity-50"
        >
          {t('admin.dashboard.licenses.seatsSave')}
        </button>
      )}
      {dirty && stranding && (
        // basis-full: its own line inside the wrapping flex row, so a warning
        // about breaking machines never ends up as a fragment at the end of
        // the seat controls.
        <p className="basis-full text-[11px] text-badge-red-text leading-[1.5] mt-[2px]">
          {t('admin.dashboard.licenses.seatsStranding', {
            used: license.usedSeats,
            requested: clamp(value)
          })}
        </p>
      )}
    </>
  );
};

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-badge-green-bg border-badge-green-text text-badge-green-text',
  suspended: 'bg-amber-100 border-amber-500 text-amber-700',
  revoked: 'bg-badge-red-bg border-badge-red-text text-badge-red-text',
  lapsed: 'bg-surface-secondary-alt border-border-dark/[0.15] text-content-muted'
};

/**
 * Admin view of one account's Squarebit-issued licences: key, seats, status,
 * expiry, and every machine that has ever held a seat — including released
 * ones, which is the history an operator needs when a customer says a seat is
 * stuck.
 *
 * A NEW SIBLING of DashboardSection rather than an edit to it: DashboardSection
 * is 1,400 lines carrying every existing account operation, and a regression
 * there takes out the whole operator console.
 *
 * Deliberately product-slug generic from the start, unlike the existing Eye
 * grant UI (a hardcoded `eyeAccess` boolean) — so a second licensed product
 * needs no change here.
 */
export const UserLicensesModal = ({
  user,
  onClose
}: {
  user: IAdminUser;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const queryKey = ['admin-licenses', user.id];

  const licensesQuery = useQuery({
    queryKey,
    queryFn: () => adminService.getUserLicenses(user.id),
    // The global client caches for 24h with no refetch on mount, which would
    // show an operator stale seat counts. Overridden here only.
    staleTime: 0,
    refetchOnMount: true
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: ['admin-users'] });
  };

  const issueMutation = useMutation({
    mutationFn: () => adminService.issueLicense(user.id, EYE_SLUG, 1),
    onSuccess: () => {
      toast.success(t('admin.dashboard.licenses.toast.issued'));
      invalidate();
    },
    onError: (err: unknown) =>
      toast.error(
        serverMessage(err) ?? t('admin.dashboard.licenses.toast.issueError')
      )
  });

  const updateMutation = useMutation({
    mutationFn: ({
      licenseId,
      patch
    }: {
      licenseId: number;
      patch: {
        seats?: number;
        status?: string;
        reason?: string;
        revokeAccess?: boolean;
      };
    }) => adminService.updateLicense(licenseId, patch),
    onSuccess: () => {
      toast.success(t('admin.dashboard.licenses.toast.updated'));
      invalidate();
    },
    // The server's own words, not a generic failure: a refused seat drop comes
    // back naming how many machines are activated and how many to release,
    // which is the entire instruction the admin needs. Falls back to the
    // generic string only when there is no message to show (a network drop).
    onError: (err: unknown) =>
      toast.error(
        serverMessage(err) ?? t('admin.dashboard.licenses.toast.updateError')
      )
  });

  const releaseMutation = useMutation({
    mutationFn: ({
      licenseId,
      activationId
    }: {
      licenseId: number;
      activationId: number;
    }) => adminService.releaseLicenseActivation(licenseId, activationId),
    onSuccess: () => {
      toast.success(t('admin.dashboard.licenses.toast.released'));
      invalidate();
    },
    onError: (err: unknown) =>
      toast.error(
        serverMessage(err) ?? t('admin.dashboard.licenses.toast.releaseError')
      )
  });

  const busy =
    issueMutation.isPending || updateMutation.isPending || releaseMutation.isPending;

  const licenses = licensesQuery.data ?? [];

  return (
    <>
      <div className="flex items-start justify-between mb-[16px]">
        <div>
          <h2 className="text-[18px] font-display font-bold text-content-dark">
            {t('admin.dashboard.licenses.title')}
          </h2>
          <p className="text-[13px] text-gray-900 dark:text-gray-700 mt-[2px] truncate max-w-xs">
            {user.email}
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-600 hover:text-gray-900 text-[22px] leading-none mt-[2px] font-medium"
        >
          ×
        </button>
      </div>

      {licensesQuery.isLoading ? (
        <div className="flex justify-center py-[32px]">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
        </div>
      ) : licenses.length === 0 ? (
        <p className="text-[14px] text-gray-900 dark:text-gray-700 text-center py-[24px]">
          {t('admin.dashboard.licenses.empty')}
        </p>
      ) : (
        <div className="flex flex-col gap-[14px] max-h-[420px] overflow-y-auto pr-[4px]">
          {licenses.map((license: IAdminProductLicense) => (
            <div
              key={license.id}
              className="px-[14px] py-[12px] rounded-[12px] border border-border-dark/[0.08] bg-surface-base"
            >
              <div className="flex items-center gap-[8px] flex-wrap mb-[8px]">
                <span className="font-mono text-[12px] text-content-dark">
                  {license.key}
                </span>
                <span
                  className={`inline-flex items-center px-[7px] py-px rounded-[4px] border text-[10px] font-semibold uppercase tracking-wide ${
                    STATUS_STYLES[license.status] ?? STATUS_STYLES.lapsed
                  }`}
                >
                  {t(`admin.dashboard.licenses.status.${license.status}`, {
                    defaultValue: license.status
                  })}
                </span>
                {/* Which key this is. An account can hold a free one, a comped
                    one and a bought one at once; an admin changing seats or
                    revoking needs to know which is which. */}
                <span className="inline-block px-[7px] py-[2px] rounded-[5px] border border-border-dark/[0.15] bg-surface-secondary-alt font-mono text-[9px] tracking-[0.08em] uppercase text-content-muted">
                  {t(`management.eye.license.tier.${license.source}`, {
                    defaultValue: license.source
                  })}
                </span>
                <span className="text-[11px] text-gray-500">
                  {license.productSlug}
                </span>
              </div>

              <div className="flex items-center gap-[8px] flex-wrap mb-[10px]">
                <SeatEditor
                  license={license}
                  busy={busy}
                  onSave={(seats) =>
                    updateMutation.mutate({
                      licenseId: license.id,
                      patch: { seats }
                    })
                  }
                />
                <span className="text-[11px] text-gray-500">
                  {license.expiresAt
                    ? t('admin.dashboard.licenses.expires', {
                        date: formatDateTime(license.expiresAt)
                      })
                    : t('admin.dashboard.licenses.perpetual')}
                </span>
                {license.status === 'revoked' ? (
                  <button
                    disabled={busy}
                    onClick={() =>
                      updateMutation.mutate({
                        licenseId: license.id,
                        patch: { status: 'active' }
                      })
                    }
                    className="ml-auto inline-flex items-center px-[8px] py-[3px] rounded-[6px] text-[11px] font-medium bg-badge-green-bg border border-badge-green-text text-badge-green-text hover:brightness-95 transition-colors disabled:opacity-50"
                  >
                    {t('admin.dashboard.licenses.reinstate')}
                  </button>
                ) : (
                  <button
                    disabled={busy}
                    onClick={() => {
                      // Ask WHY, and offer to cut the download off too.
                      //
                      // prompt() rather than a modal deliberately: this is an
                      // admin-only action taken rarely, and a reason recorded
                      // in one line beats a nicer dialog that ships later.
                      // Cancel (null) aborts; an empty string does not, so a
                      // hurried revoke still happens and simply records no
                      // reason rather than being silently dropped.
                      const reason = window.prompt(
                        t('admin.dashboard.licenses.revokeReasonPrompt')
                      );
                      if (reason === null) return;
                      const alsoAccess = window.confirm(
                        t('admin.dashboard.licenses.revokeAccessConfirm')
                      );
                      {
                        updateMutation.mutate({
                          licenseId: license.id,
                          patch: {
                            status: 'revoked',
                            reason: reason.trim() || undefined,
                            revokeAccess: alsoAccess
                          }
                        });
                      }
                    }}
                    className="ml-auto inline-flex items-center px-[8px] py-[3px] rounded-[6px] text-[11px] font-medium bg-badge-red-bg border border-badge-red-text text-badge-red-text hover:brightness-95 transition-colors disabled:opacity-50"
                  >
                    {t('admin.dashboard.licenses.revoke')}
                  </button>
                )}
              </div>

              {license.activations.length === 0 ? (
                <p className="text-[11px] text-gray-500">
                  {t('admin.dashboard.licenses.noMachines')}
                </p>
              ) : (
                <div className="flex flex-col gap-[4px]">
                  {license.activations.map((activation) => (
                    <div
                      key={activation.id}
                      className={`flex items-center gap-[8px] px-[10px] py-[7px] rounded-[8px] border text-[11px] ${
                        activation.releasedAt
                          ? 'bg-surface-secondary-alt border-border-dark/[0.06] opacity-60'
                          : 'bg-surface-card border-border-dark/[0.08]'
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-content-dark font-medium truncate">
                          {activation.machineLabel ||
                            t('admin.dashboard.licenses.unnamedMachine')}
                        </div>
                        <div className="text-gray-500 font-mono truncate">
                          {[activation.os, activation.hostApp, activation.appVersion]
                            .filter(Boolean)
                            .join(' · ') || '—'}
                        </div>
                        {activation.activationMode === 'offline' && (
                          <div className="mt-[2px] flex items-center gap-[5px] flex-wrap">
                            <span className="inline-flex items-center px-[5px] py-px rounded-[4px] bg-surface-hover border border-blue-500 text-[9.5px] font-semibold uppercase text-blue">
                              {t('admin.dashboard.licenses.offlineBadge')}
                            </span>
                            {/* Revocation cannot reach an air-gapped machine,
                                so this date IS the exposure window. Surface it
                                rather than let it be discovered. */}
                            {activation.tokenExpiresAt && (
                              <span className="text-gray-500">
                                {t('admin.dashboard.licenses.offlineExpires', {
                                  date: formatDateTime(activation.tokenExpiresAt)
                                })}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <span className="text-gray-500 shrink-0">
                        {formatDateTime(activation.lastSeenAt)}
                      </span>
                      {activation.releasedAt ? (
                        <span className="text-gray-500 shrink-0">
                          {t('admin.dashboard.licenses.released')}
                        </span>
                      ) : (
                        <button
                          disabled={busy}
                          onClick={() =>
                            releaseMutation.mutate({
                              licenseId: license.id,
                              activationId: activation.id
                            })
                          }
                          className="shrink-0 inline-flex items-center px-[7px] py-[2px] rounded-[6px] font-medium bg-badge-red-bg border border-badge-red-text text-badge-red-text hover:brightness-95 transition-colors disabled:opacity-50"
                        >
                          {t('admin.dashboard.licenses.release')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-[10px] justify-end mt-[18px]">
        <button
          onClick={onClose}
          className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium text-gray-900 dark:text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
        >
          {t('common.close')}
        </button>
        <button
          disabled={busy}
          onClick={() => issueMutation.mutate()}
          className="px-[18px] py-[8px] rounded-[10px] text-[14px] font-medium bg-blue text-surface-base hover:bg-surface-popular disabled:opacity-50 transition-colors"
        >
          {issueMutation.isPending
            ? t('admin.dashboard.licenses.issuing')
            : t('admin.dashboard.licenses.issue')}
        </button>
      </div>
    </>
  );
};
